import { Component, OnInit, ViewChild } from '@angular/core';
import BigNumber from 'bignumber.js';
import {
  ChartComponent,
} from 'ng-apexcharts';
import { take } from 'rxjs/operators';
import { GRAPH_DATA_TRADE, TRADE_MARKET_ADDRESS } from 'src/app/angular-wallet-base/constants';
import { SmallToCryptoPipe } from 'src/app/angular-wallet-base/pipes/smalltocrypto/smalltocrypto.pipe';
import { AppModeService } from 'src/app/angular-wallet-base/services/app-mode.service';
import { NetworkService } from 'src/app/angular-wallet-base/services/network.service';
import { getBaseCurrency } from 'src/app/angular-wallet-base/store/userPreferences';
import { getKeychain, getTradeOrders, getTradeOrdersHistory } from 'src/app/angular-wallet-base/store/wallet';
import {
  getCurrentNetworkIcon,
  getCurrentNetworkLabel,
  getTokenBalance,
  getTokenFiatValue,
  getTokenInfoArray,
} from 'src/app/angular-wallet-base/utils';
import { MenuController, NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '../../angular-wallet-base/store/appState';
import { SubscriptionListenerComponent } from '../../angular-wallet-base/SubscriptionListenerComponent';
import { SubTradeComponent } from './trade/trade.component';
import { OneInchService } from 'src/app/angular-wallet-base/services/one-inch.service';
import { inspect } from 'util';
import { Logger } from 'src/app/angular-wallet-base/services/logger.service';
import { StorageService } from 'src/app/angular-wallet-base/services/storage.service';
import { GraphService } from 'src/app/angular-wallet-base/services/graph.service';
import { EnvironmentService } from 'src/app/angular-wallet-base/services/environment.service';

@Component({
  selector: 'app-trade',
  templateUrl: './trade-home.component.html',
  styleUrls: ['./trade-home.component.scss'],
})
export class TradeHomeComponent extends SubscriptionListenerComponent implements OnInit {

  @ViewChild(SubTradeComponent) tradeHome: SubTradeComponent;
  @ViewChild("chart", { static: false }) chart: ChartComponent;

  public isDesktopMode;
  public selectedDate = "";
  public showChart = false;
  public activeOptionButton = "ALL";
  public currentTab = 'trade';
  public chartOptions;
  public vaultChartOptions;
  public chartYAxis = [];
  public updateOptionsData;
  public totalBalance = 0.00;
  public currentBaseCurrency: string;
  public progressBar: boolean = false;
  public showGraph: boolean = true;
  public showAdvancedGraph: boolean = false;
  public pair: string;
  public markets = TRADE_MARKET_ADDRESS;
  public orders = [];
  public activeOrders = [];
  public usdcFiatVaule = 0.00;
  public graphDataSet: any;
  public tokens: any[];
  public orderProgress: boolean = true;
  public loadingProgress: boolean = false;
  public waveSvg = 'trade';
  public tradeFeature =  this.environmentService.service('trade');
  public isMoveEnabled = this.environmentService.service('move');

  constructor(
    public appMode: AppModeService,
    protected store: Store<AppState>,
    private menu: MenuController,
    private translate: TranslateService,
    private networkManager: NetworkService,
    private smallToCrypto: SmallToCryptoPipe,
    private nav: NavController,
    private oneInchService: OneInchService,
    private storage: StorageService,
    private graphService: GraphService,
    public environmentService: EnvironmentService
  ) {
    super(store);
    this.chartYAxis = this.graphService.chartYAxis.getValue();
    this.updateOptionsData = this.graphService.updateOptionsData.getValue();
    const tabInstance = this;
    this.appMode.isDesktopMode.subscribe((mode) => (this.isDesktopMode = mode));
    // fetching graph data after each transactions
    this.graphService.chartCount.subscribe((count) => {
      this.fetchStorageData();
    });
  }

  initChart() {
    this.chartOptions = this.vaultChartOptions;
    this.showChart = this.graphDataSet ? true : false;
    setTimeout(() => {
      this.updateOptions("ALL");
    }, 1000);
  }

  async fetchStorageData() {
    this.graphDataSet = await this.storage.get(GRAPH_DATA_TRADE);
    if (this.graphDataSet) {
      this.vaultChartOptions.series[0].data = this.graphDataSet;
      this.initChart();
    }
  }

  async ngOnInit() {
    this.vaultChartOptions = await this.graphService.getTradePageChartOptions(this.isDesktopMode, this);
    this.initChart();
    this.currentBaseCurrency = await this.store.select(getBaseCurrency).pipe(take(1)).toPromise();

    // filter tokens that support trade
    this.subscriptions.add(
      this.store.select(getKeychain).subscribe(async (keychain) => {
        if (!keychain) {
          // Prevent error on wallet reset.
          return;
        }
        const tokens = await Promise.all(
          getTokenInfoArray(keychain)
            .filter(chain => (['AVAX'].includes(chain.baseChainSymbol)))
            .sort((a, b) => a.symbol.charCodeAt(0) - b.symbol.charCodeAt(0))
            .map(async token => {
              const tokenCopy: any = { ...token };
              tokenCopy.networkLabel = await getCurrentNetworkLabel({
                tokenInfo: tokenCopy,
                networkManager: this.networkManager,
                translateService: this.translate
              });
              tokenCopy.iconChain = await getCurrentNetworkIcon(tokenCopy.symbol, tokenCopy.baseChainSymbol);
              tokenCopy.showIconInMain = true;
              if (token.symbol === 'USDC') {
                this.usdcFiatVaule = Number(token.fiatValue);
              }
              return tokenCopy;
            })
        );

        this.tokens = tokens;
        this.refreshTotalBalance();
      })
    );

    this.subscriptions.add(
      this.oneInchService.orderProgress.subscribe(status => {
        this.orderProgress = status;
      })
    );
    this.getAllOrders();
  }

  async ionViewWillEnter() {
    await this.fetchStorageData();
  }
  
  // calculate total fiat balance using only tokens that have an available fiatValue
  private async refreshTotalBalance() {
    let newTotalBalance = 0.00;
    for (const token of this.tokens) {
      try {
        if (token.fiatValue) {
          let num = getTokenFiatValue(token.fiatValue, getTokenBalance(token));
          num = await this.smallToCrypto.transform(num, { type: token.baseChainSymbol, precision: token.precision });
          newTotalBalance += new BigNumber(num).toNumber();
        }
      } catch (e) {
        Logger.error('TradeHome - refreshTotalBalance- token', token, inspect(e));
      }
    }
    this.totalBalance = newTotalBalance;
  }

  public updateOptions(option: any): void {
    this.selectedDate = "";
    this.activeOptionButton = option;
    const updatedOptions = this.updateOptionsData[option];
    const updatedData = [];
    for (const data of this.graphDataSet) {
      if (data[0] <= updatedOptions.xaxis.max && data[0] >= updatedOptions.xaxis.min) {
        updatedData.push(data);
      }
    }
    if (updatedData.length > 0) {
      updatedOptions.xaxis.max = updatedData[0][0];
      updatedOptions.xaxis.min = updatedData[updatedData.length - 1][0];
    }
    this.chart.updateOptions(updatedOptions, false, true, true);
  }

  async ngAfterViewInit() {
    await this.menu.enable(true, 'sideMenu');
  }

  async ionViewDidEnter() {
    // MOBILE: ngAfterViewInit and ngOnInit appear to not fire after authenticate so we use ionViewDidEnter here
    await this.menu.enable(true, 'sideMenu');
  }

  segmentChanged(event) {

    if (event.detail.value === 'move') {
      try { this.loadingProgress = true; } catch (e) { }
    }

    if (event.detail.value !== 'trade') {
      try { this.tradeHome.resetForm(); } catch (e) { }
    }
  }

  resetChart() {
    this.activeOptionButton = 'ALL';
    this.selectedDate = '';
    this.showChart = false;
    this.initChart();
  }

  showGraphHandler(showGraph: boolean) {
    if (!this.isDesktopMode) {
      this.showGraph = showGraph;
    }
  }

  showAdvancedGraphHandler(response) {
    this.pair = response.pair;
    this.showAdvancedGraph = response.showAdvancedGraph;
  }

  toAdvancedGraph() {
    if (this.pair) {
      this.pair = this.pair.split('_').reverse().join('_');
      this.nav.navigateForward(['trade/advanced-graph', this.pair]);
    }
  }

  // get all orders (Avalnche)
  async getAllOrders() {
    this.store.select(getTradeOrders).subscribe(activeOrders => {
      this.activeOrders = activeOrders;
    });

    this.store.select(getTradeOrdersHistory).subscribe(orders => {
      this.orders = orders;
    });
  }

  goBack() {
    this.nav.back();
  }

  stopMoveLoader() {
    this.loadingProgress = false;
  }
}
